'use client';

import * as React from 'react';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { usePathname } from 'next/navigation';
import type { NavigationItem } from '@/components/common/static-section/static';
import { useNavigationMenu, type NavigationNode } from '@/hooks/useNavigationMenu';
import { useDict } from '@/app/[lang]/providers/DictionaryProvider';

type DynamicBreadcrumbsProps = {
  navigation?: NavigationItem[];
  currentTitle?: string;
};

/** ====== i18n config (harus sinkron dg middleware) ====== */
const LOCALES = ['id', 'en'] as const;
type Locale = (typeof LOCALES)[number];
const DEFAULT_LOCALE: Locale = 'id';

function detectLocaleFromPath(pathname: string): Locale {
  const first = pathname.split('/').filter(Boolean)[0];
  return (LOCALES.find((l) => l === first) ?? DEFAULT_LOCALE) as Locale;
}

export function DynamicBreadcrumbs({ navigation, currentTitle }: DynamicBreadcrumbsProps) {
  const pathname = usePathname() || '/';
  const locale = detectLocaleFromPath(pathname);
  const dict = useDict();
  const { data: navigationMenu } = useNavigationMenu();

  // Map navigation node id → true bila node tsb adalah grup (punya children).
  // Grup tidak punya halaman/URL sendiri → segment breadcrumb tidak boleh jadi link.
  const nodeIsGroup = React.useMemo(() => {
    const map = new Map<number, boolean>();
    const walk = (nodes: NavigationNode[]) => {
      nodes.forEach((node) => {
        map.set(node.id, node.children.length > 0);
        if (node.children.length > 0) walk(node.children);
      });
    };
    if (navigationMenu) walk(navigationMenu);
    return map;
  }, [navigationMenu]);

  // Build breadcrumb trail from navigation array
  const trail = React.useMemo(() => {
    const crumbs: Array<{ label: string; href?: string }> = [];
    
    // Add home
    crumbs.push({
      label: dict.common?.home || 'Home',
      href: `/${locale}`
    });

    // If navigation array exists, build trail from it
    if (navigation && Array.isArray(navigation) && navigation.length > 0) {
      // Navigation is already ordered from parent to child.
      // Segment hanya dibuat link bila punya link URL aktif dari navigation:
      // - slug tidak kosong, dan
      // - bukan grup (node tanpa halaman sendiri)
      const linkableSlugs: string[] = [];

      navigation.forEach((nav, index) => {
        const isLast = index === navigation.length - 1;
        const slug = typeof nav.slug === 'string'
          ? nav.slug.trim().replace(/^\/+|\/+$/g, '')
          : '';
        const isGroup = nodeIsGroup.get(nav.id);
        const hasLinkUrl = slug.length > 0 && isGroup === false;

        if (hasLinkUrl) linkableSlugs.push(slug);

        // Build href only from linkable slugs up to this point
        const href = hasLinkUrl && !isLast
          ? `/${locale}/${linkableSlugs.join('/')}`
          : undefined; // Last item / segment tanpa URL = plain text

        crumbs.push({
          label: nav.title,
          href
        });
      });
    } else if (currentTitle) {
      // Fallback: just add current title without href
      crumbs.push({
        label: currentTitle
      });
    }

    return crumbs;
  }, [navigation, currentTitle, locale, nodeIsGroup, dict.common?.home]);

  if (!trail?.length || trail.length === 1) {
    // Don't show breadcrumbs if only home exists
    return null;
  }

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {trail.map((crumb, i) => {
          const isLast = i === trail.length - 1;
          return [
            <BreadcrumbItem key={'item-' + i}>
              {crumb.href && !isLast ? (
                <BreadcrumbLink href={crumb.href}>{crumb.label}</BreadcrumbLink>
              ) : (
                <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
              )}
            </BreadcrumbItem>,
            !isLast && <BreadcrumbSeparator key={'sep-' + i} />,
          ];
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
